import {
    ReactNode,
    ReactElement,
    useState,
    isValidElement,
    cloneElement,
    Children,
    KeyboardEvent,
    MouseEvent,
} from 'react';
import { cn } from '../../utils';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export type AccordionProps = {
    title: string;
    subtitle?: string;
    children?: ReactNode;
    className?: string;
    leadingIcon?: ReactNode;
    onClick?: (
        e: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
    ) => void;
    disabled?: boolean;
    action?: ReactNode;
    variant?: AccordionVariants;
    borderBottom?: boolean;
    chevronLeft?: boolean;
};

export type AccordionVariants = 'default' | 'card';

type AccordionMenuProps = {
    children: ReactNode;
    className?: string;
};

function AccordionMenu({
    children,
    className = '',
    ...rest
}: AccordionMenuProps) {
    return (
        <div
            className={cn(
                'alli-flex alli-flex-col alli-overflow-hidden alli-transition-all alli-duration-500 alli-ease-in-out',
                className,
            )}
            {...rest}>
            {children}
        </div>
    );
}

type AccordionItemProps = {
    children: ReactNode;
    className?: string;
};

function AccordionItem({
    children,
    className = '',
    ...rest
}: AccordionItemProps) {
    return (
        <div className={cn('alli-w-full', className)} {...rest}>
            {children}
        </div>
    );
}

function Accordion({
    title = '',
    subtitle = '',
    leadingIcon,
    action,
    children,
    className = '',
    disabled = false,
    onClick,
    borderBottom = false,
    variant = 'default',
    chevronLeft = false,
    ...rest
}: AccordionProps) {
    const [isOpen, setIsOpen] = useState(false);

    const variantStyles = variants[variant];

    const containerClasses = cn(
        variantStyles.container.base,
        {
            [variantStyles.container.open]: isOpen,
            [variantStyles.container.closed]: !isOpen,
            [variantStyles.container.borderBottom]: borderBottom,
            [variantStyles.container.disabled]: disabled,
        },
        className,
    );

    const buttonClasses = cn(variantStyles.button.base, className, {
        [variantStyles.button.open]: isOpen,
        [variantStyles.button.closed]: !isOpen,
        [variantStyles.button.disabled]: disabled,
    });

    const onInteraction = (
        e: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
    ) => {
        if (disabled) {
            return;
        } else {
            e.preventDefault();
            setIsOpen(!isOpen);
            onClick && onClick(e);
        }
    };

    const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.code == 'Space' && document.activeElement?.nodeName != 'BUTTON') {
            onInteraction(e);
        }
    };

    return (
        <div className={containerClasses} {...rest}>
            <div
                onClick={e => onInteraction(e)}
                tabIndex={disabled ? -1 : 0}
                role="button"
                onKeyDown={onKeyDown}
                aria-disabled={disabled}
                className={buttonClasses}>
                <div className="alli-flex alli-flex-1 alli-justify-start alli-gap-2.5 alli-transition-all alli-duration-700 alli-ease-in-out">
                    {(leadingIcon || chevronLeft) && (
                        <div className="alli-flex alli-items-center alli-justify-center alli-gap-2.5">
                            {chevronLeft && (
                                <ChevronDownIcon
                                    className={cn(
                                        'alli-h-5 alli-w-5 alli-self-center alli-text-gray-700 alli-transition-transform alli-duration-200',
                                        {
                                            'alli-rotate-180 alli-transform':
                                                isOpen,
                                        },
                                    )}
                                />
                            )}
                            {leadingIcon &&
                            disabled &&
                            isValidElement(leadingIcon)
                                ? (() => {
                                      const icon = leadingIcon as ReactElement;
                                      return cloneElement(icon, {
                                          className: cn(
                                              icon.props.className,
                                              'alli-text-gray-600',
                                          ),
                                      });
                                  })()
                                : leadingIcon}
                        </div>
                    )}
                    <div className="alli-flex alli-flex-col alli-items-start alli-gap-1.5">
                        <span
                            className={cn('alli-text-sm alli-font-medium', {
                                'alli-text-gray-600': disabled,
                                'alli-text-blue-gray-700': !disabled,
                            })}>
                            {title}
                        </span>
                        {subtitle && (
                            <span className="alli-text-xs alli-font-normal alli-text-gray-700">
                                {subtitle}
                            </span>
                        )}
                    </div>
                </div>
                <div className="alli-flex alli-items-center alli-justify-start alli-gap-2.5">
                    {action}
                    {!chevronLeft && (
                        <ChevronDownIcon
                            className={cn(
                                'alli-h-5 alli-w-5 alli-self-center alli-text-gray-700 alli-transition-transform alli-duration-200',
                                {
                                    'alli-rotate-180 alli-transform': isOpen,
                                },
                            )}
                        />
                    )}
                </div>
            </div>
            {isOpen &&
                (() => {
                    const childrenArray = Children.toArray(children);

                    return childrenArray.map((child, idx, arr) => {
                        if (!isValidElement(child)) return null;

                        const isLast = idx === arr.length - 1;
                        const isCard = variant === 'card';

                        if (child.type === AccordionMenu) {
                            return cloneElement(child, {
                                ...child.props,
                                key: idx,
                                className: cn(
                                    className,
                                    child.props.className,
                                    isCard && isLast && 'alli-rounded-b-md',
                                ),
                            });
                        }

                        return cloneElement(child, {
                            ...child.props,
                            key: idx,
                            className: cn(
                                child.props.className,
                                isCard && isLast && 'alli-rounded-b-md',
                            ),
                        });
                    });
                })()}
        </div>
    );
}

const variants = {
    default: {
        container: {
            base: 'alli-flex alli-w-full alli-flex-col',
            open: '',
            closed: '',
            borderBottom: 'alli-border-b alli-border-gray-400',
            disabled: '',
        },
        button: {
            base: 'alli-flex alli-w-full alli-justify-between alli-p-4 hover:alli-bg-gray-100 alli-cursor-pointer',
            open: 'alli-bg-blue-gray-50',
            closed: '',
            disabled: 'alli-cursor-not-allowed',
        },
    },
    card: {
        container: {
            base: 'alli-flex alli-w-full alli-flex-col alli-mb-2 alli-rounded-md alli-border alli-border-gray-400',
            open: '',
            closed: '',
            borderBottom: '',
            disabled: '',
        },
        button: {
            base: 'alli-flex alli-w-full alli-justify-between alli-p-4 hover:alli-bg-gray-100 alli-cursor-pointer',
            open: 'alli-bg-blue-gray-50 alli-rounded-t-md',
            closed: 'hover:alli-rounded-md',
            disabled: 'alli-cursor-not-allowed',
        },
    },
};

Accordion.Menu = AccordionMenu;
Accordion.Item = AccordionItem;

export default Accordion;
